import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Table, Spin, Tooltip, Button, Tag, Space, Typography, Select, Progress } from "antd";
import {
  LoadingOutlined,
  CloudDownloadOutlined,
  DownloadOutlined,
  FilePdfOutlined,
  BankOutlined,
  ShopOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import { ColumnsType } from "antd/es/table";
import { PageLayout, PageHeader, SectionCard, TableToolbar, FilterCriteria, ToolbarAction } from "../../Common/Layout";
import { IXcelifyAdminListItem } from "../../Services/XcelifyExtractionService";
import { useXcelifyExtraction } from "./useXcelifyExtraction";
import { IPractices } from "../../Context/PracticeContext";

const { Text } = Typography;

const formatFileSize = (bytes?: number): string => {
  if (!bytes || bytes === 0) return "N/A";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
};

const XcelifyExtraction: React.FC = () => {
  const navigate = useNavigate();

  const {
    loading,
    downloading,
    downloadingId,
    allPracticeIds,
    practiceMap,
    companyList,
    loadingCompanies,
    bankList,
    loadingBanks,
    selectedPracticeId,
    selectedClientId,
    selectedBankId,
    items,
    totalItems,
    currentPage,
    pageSize,
    selectedRowKeys,
    extractionProgress,
    setCurrentPage,
    setPageSize,
    handlePracticeChange,
    handleCompanyChange,
    handleBankChange,
    handleSearch,
    handleRefresh,
    handleSingleDownload,
    handleBulkExtraction,
    handleCancelExtraction,
    handleRowSelectionChange,
  } = useXcelifyExtraction();

  const columns: ColumnsType<IXcelifyAdminListItem> = useMemo(
    () => [
      {
        title: "S.No.",
        dataIndex: "sno",
        key: "sno",
        width: 60,
        render: (val: number) => <span>{val}</span>,
      },
      {
        title: "Ref Number",
        dataIndex: "number",
        key: "number",
        width: "100px",
        render: (text: string) => (
          <Tag color="blue" style={{ fontWeight: 600 }}>
            {text || "N/A"}
          </Tag>
        ),
      },
      {
        title: "Company",
        dataIndex: "company",
        key: "company",
        width: "20%",
        render: (comp?: { id: string; name: string }) => (
          <Space>
            <ShopOutlined style={{ color: "#1890ff" }} />
            <Text strong>{comp?.name || "N/A"}</Text>
          </Space>
        ),
      },
      {
        title: "Bank",
        dataIndex: "bank",
        key: "bank",
        width: "18%",
        render: (bank?: { id: string; name: string }) => (
          <Space>
            <BankOutlined style={{ color: "#52c41a" }} />
            <span>{bank?.name || "N/A"}</span>
          </Space>
        ),
      },
      {
        title: "File Details",
        dataIndex: "file",
        key: "file",
        // width: "25%",
        render: (file?: { name: string; length: number }) => (
          <Space>
            <FilePdfOutlined style={{ color: "#ff4d4f", fontSize: 16 }} />
            <span style={{ fontWeight: 500 }}>{file?.name || "N/A"}</span>
            <Text type="secondary" style={{ fontSize: 11 }}>
              ({formatFileSize(file?.length)})
            </Text>
          </Space>
        ),
      },
      {
        title: "Practice",
        dataIndex: "practiceId",
        key: "practiceId",
        width: "15%",
        render: (practiceId: number) => (
          <span style={{ display: "flex", gap: 6, alignItems: "center" }}>
            {/* <strong>{allPracticeIds[practiceId].label || `Practice ${practiceId}`}</strong> */}

            <strong>{practiceMap[practiceId] || `Practice ${practiceId}`}</strong>
            <Tag color="purple" style={{ fontSize: 11 }}>
              ID: {practiceId}
            </Tag>
          </span>
        ),
      },
      {
        title: "Action",
        key: "action",
        width: "60px",
        render: (_, record: IXcelifyAdminListItem) => {
          const isThisDownloading = downloadingId === record.id;
          return (
            <Tooltip title={isThisDownloading ? "Downloading file..." : "Download original blob file"}>
              <Button
                type="text"
                loading={isThisDownloading}
                icon={!isThisDownloading ? <DownloadOutlined style={{ color: "#1890ff", fontSize: 16 }} /> : undefined}
                onClick={() => handleSingleDownload(record)}
              />
            </Tooltip>
          );
        },
      },
    ],
    // 
    [practiceMap, downloadingId, handleSingleDownload]
  );

  const primaryActions: ToolbarAction[] = useMemo(
    () => [
      {
        key: "extractSelected",
        label: `Selected ${selectedRowKeys.length > 0 ? `(${selectedRowKeys.length})` : ""}`,
        icon: <CloudDownloadOutlined />,
        type: "primary",
        disabled: selectedRowKeys.length === 0,
        loading: downloading,
        onClick: () => handleBulkExtraction(false),
      },
      {
        key: "extractAll",
        label: `Extract All (${totalItems})`,
        icon: <CloudDownloadOutlined />,
        type: "default",
        disabled: totalItems === 0,
        loading: downloading,
        onClick: () => handleBulkExtraction(true),
      },
    ],
    [selectedRowKeys.length, totalItems, downloading, handleBulkExtraction]
  );

  const rowSelection = useMemo(
    () => ({
      selectedRowKeys,
      onChange: handleRowSelectionChange,
    }),
    [selectedRowKeys, handleRowSelectionChange]
  );

  const renderHeaderSubtitle = () => {
    if (!extractionProgress.active) {
      return "Extract single or multiple bank statement files from Azure Blob Storage";
    }

    return (
      <div style={{ maxWidth: 480, marginTop: 4 }}>
        <Space style={{ marginBottom: 2, width: "100%", justifyContent: "space-between" }}>
          <Space>
            <Text strong style={{ fontSize: 13, color: "#1890ff" }}>
              Bulk Extraction Progress: {extractionProgress.percent}%
            </Text>
            <Text type="secondary" style={{ fontSize: 12 }}>
              ({extractionProgress.processed} / {extractionProgress.total} files processed)
            </Text>
          </Space>
          <Button
            type="link"
            danger
            size="small"
            icon={<CloseCircleOutlined />}
            onClick={handleCancelExtraction}
            style={{ padding: 0 }}
          >
            Cancel
          </Button>
        </Space>
        <Progress
          percent={extractionProgress.percent}
          status="active"
          strokeColor={{ "0%": "#108ee9", "100%": "#52c41a" }}
          size="small"
        />
      </div>
    );
  };

  return (
    <PageLayout>
      <PageHeader
        title="Xcelify Extraction"
        subtitle={renderHeaderSubtitle()}
        actions={[
          <Select
            key="module-switcher"
            value="xcelify"
            onChange={(val) => {
              if (val === "invoice") navigate("/invoice-extraction");
            }}
            options={[
              { value: "xcelify", label: "Xcelify Extraction" },
              { value: "invoice", label: "Invoice Extraction" },
            ]}
            style={{ width: 180 }}
          />,
        ]}
      />
      <SectionCard noPadding>
        <div style={{ padding: "0 16px" }}>
          <TableToolbar
            primaryActions={primaryActions}
            onSearch={handleSearch}
            searchPlaceholder="Search Ref No, Company, Bank, File..."
            onRefresh={handleRefresh}
            filterCriteria={[
              {
                key: "clientId",
                label: "Company",
                type: "select" as const,
                placeholder: !selectedPracticeId
                  ? "Select practice first"
                  : loadingCompanies
                    ? "Loading..."
                    : "Select Company",
                options: companyList.map((c) => ({ value: c.id, label: c.name })),
              },
              {
                key: "bankId",
                label: "Bank",
                type: "select" as const,
                placeholder: !selectedPracticeId
                  ? "Select practice first"
                  : loadingBanks
                    ? "Loading..."
                    : "Select Bank",
                options: bankList.map((b) => ({ value: b.id, label: b.name })),
              },
            ] as FilterCriteria[]}
            activeFilters={{
              clientId: selectedClientId,
              bankId: selectedBankId,
            }}
            onFiltersChange={(filters) => {
              handleCompanyChange(filters.clientId);
              handleBankChange(filters.bankId);
            }}
            extra={
              <Select
                showSearch
                allowClear
                placeholder="Select Practice"
                style={{ width: 200 }}
                value={selectedPracticeId}
                onChange={handlePracticeChange}
                optionFilterProp="label"
                filterOption={(input, option: any) =>
                  (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
                }
                options={allPracticeIds.map((p: IPractices) => ({
                  value: p.value,
                  label: p.label + " (ID: " + p.value + ")",
                }))}
              />
            }
          />
        </div>
        <Table
          dataSource={items}
          columns={columns}
          rowSelection={rowSelection}
          rowKey="id"
          size="small"
          scroll={{ x: 1000, y: window.innerHeight - 270 }}
          loading={{
            spinning: loading || downloading,
            indicator: <Spin indicator={<LoadingOutlined />} />,
          }}
          className="border-top-table"
          pagination={{
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} records`,
            current: currentPage,
            pageSize: pageSize,
            showSizeChanger: true,
            total: totalItems,
            pageSizeOptions: ["15", "25", "50", "100"],
          }}
          onChange={(paging) => {
            if (paging?.current !== undefined && paging?.pageSize !== undefined) {
              setCurrentPage(paging.current);
              setPageSize(paging.pageSize);
            }
          }}
        />
      </SectionCard>
    </PageLayout>
  );
};

export default XcelifyExtraction;
